import * as React from 'react';
import { Alert, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { Confirm, Identifier, useNotify } from 'react-admin';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ContractSignerParty, ProjectContractSigning } from '@/types';
import { QuoteAttachment } from '@/utils/quote_attachment_utils';
import { QuoteAttachmentPreviewDialog } from '@/components/QuoteAttachmentPreviewDialog';
import { formatFlowDate } from '../flow/projectFlowUtils';
import {
    cancelContractSigning,
    ContractSigningApiError,
    docusealSigningUrl,
    fetchProjectContractSigning,
    fetchSignedContractPdfUrl,
    startContractSigning,
} from './contractSigningApi';
import { ContractSignerDialog } from './ContractSignerDialog';

// Stub attachment: with sourceUrl set, the dialog previews that URL and only reads
// name/filename/content_type (it never hits the quote-attachments API).
const SIGNED_CONTRACT_ATTACHMENT: QuoteAttachment = {
    id: 'signed-contract',
    name: 'Signed contract',
    filename: 'Signed-Contract.pdf',
    content_type: 'application/pdf',
};

// Lazy-loaded so the embed bundle is only fetched by users who actually sign.
const DocusealForm = React.lazy(() =>
    import('@docuseal/react').then((module) => ({ default: module.DocusealForm })),
);

/** After the embed reports completion, the webhook may lag; poll until the MRT status flips. */
const POLL_INTERVAL_MS = 3000;
const POLL_WINDOW_MS = 30000;

export interface ContractSigningPanelProps {
    projectId: Identifier;
    /** Called when the signing status changes, so the parent can refetch the project flow. */
    onChanged?: () => void;
}

export const ContractSigningPanel: React.FC<ContractSigningPanelProps> = ({
    projectId,
    onChanged,
}) => {
    const queryClient = useQueryClient();
    const notify = useNotify();
    const queryKey = ['contract-signing', projectId];
    const [pollDeadline, setPollDeadline] = React.useState<number | null>(null);
    const [starting, setStarting] = React.useState(false);
    const [pickerOpen, setPickerOpen] = React.useState(false);
    const [cancelling, setCancelling] = React.useState(false);
    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const [startConfirmOpen, setStartConfirmOpen] = React.useState(false);
    const [previewOpen, setPreviewOpen] = React.useState(false);
    const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
    const [previewLoading, setPreviewLoading] = React.useState(false);

    const { data, error, isPending, refetch } = useQuery<ProjectContractSigning>({
        queryKey,
        queryFn: () => fetchProjectContractSigning(projectId),
        refetchInterval: (query) => {
            if (pollDeadline == null || Date.now() > pollDeadline) return false;
            const signing = query.state.data?.signing;
            if (!signing || signing.mrt_status === 'done') return false;
            return POLL_INTERVAL_MS;
        },
    });

    const signing = data?.signing ?? null;

    // Notify the parent when the signing state moves (start, either signature, completion),
    // so the Contract checklist's contract.signed step is refetched too.
    const statusKey = data
        ? signing
            ? `${signing.id}|${signing.mrt_status}|${signing.client_status}|${signing.completed_at ?? ''}`
            : 'none'
        : null;
    const lastStatusKey = React.useRef<string | null>(null);
    React.useEffect(() => {
        if (statusKey === null) return;
        if (lastStatusKey.current !== null && lastStatusKey.current !== statusKey) {
            onChanged?.();
        }
        lastStatusKey.current = statusKey;
    }, [statusKey, onChanged]);

    const handleStart = async (signerParty?: ContractSignerParty) => {
        setStarting(true);
        try {
            const result = await startContractSigning(projectId, signerParty);
            queryClient.setQueryData(queryKey, result);
            setPickerOpen(false);
        } catch (err) {
            if (err instanceof ContractSigningApiError && err.code === 'already_active') {
                void refetch();
            }
            // No estimate client email: let the user pick the contractor/owner instead.
            if (
                err instanceof ContractSigningApiError &&
                err.code === 'missing_client_email' &&
                !signerParty
            ) {
                setPickerOpen(true);
                return;
            }
            notify(
                err instanceof Error ? err.message : 'Failed to start contract signing',
                { type: 'error' },
            );
        } finally {
            setStarting(false);
        }
    };

    const handleCancel = async () => {
        setCancelling(true);
        try {
            const result = await cancelContractSigning(projectId);
            queryClient.setQueryData(queryKey, result);
            setConfirmOpen(false);
        } catch (err) {
            notify(
                err instanceof Error ? err.message : 'Failed to cancel contract signing',
                { type: 'error' },
            );
        } finally {
            setCancelling(false);
        }
    };

    const handleEmbedComplete = () => {
        setPollDeadline(Date.now() + POLL_WINDOW_MS);
        void refetch();
    };

    const handlePreviewSigned = async () => {
        setPreviewOpen(true);
        setPreviewLoading(true);
        try {
            setPreviewUrl(await fetchSignedContractPdfUrl(projectId));
        } catch (err) {
            notify(
                err instanceof Error ? err.message : 'Could not load the signed contract',
                { type: 'error' },
            );
            setPreviewOpen(false);
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleClosePreview = () => {
        setPreviewOpen(false);
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
    };

    if (isPending) {
        return (
            <Stack sx={{ p: 2 }}>
                <CircularProgress size={20} aria-label="Loading contract signing" />
            </Stack>
        );
    }

    if (error && !data) {
        return (
            <Alert severity="error" sx={{ m: 2 }}>
                Could not load contract signing status.
            </Alert>
        );
    }

    if (!data) return null;

    if (data.quote_id == null) {
        return (
            <Stack sx={{ p: 2, pb: 0 }}>
                <Alert severity="info">
                    No approved quote — contract signing is available once a quote is approved.
                </Alert>
            </Stack>
        );
    }

    const mrtDone = signing?.mrt_status === 'done';
    const clientDone = signing?.client_status === 'done';
    const fullySigned = !!signing && mrtDone && clientDone;
    const mrtSignerName =
        signing?.mrt_signer?.name || signing?.mrt_signer?.email || 'The MRT signer';
    const embedSlug = data.viewer_is_mrt_signer ? signing?.mrt_submitter_slug : null;

    return (
        <Stack gap={2} sx={{ p: 2, pb: 0 }} role="region" aria-label="Contract signing">
            {fullySigned && (
                <Alert severity="success">
                    Contract fully signed
                    {signing?.completed_at ? ` on ${formatFlowDate(signing.completed_at)}` : ''}.
                </Alert>
            )}

            {fullySigned && (
                <Button
                    variant="outlined"
                    onClick={() => void handlePreviewSigned()}
                    disabled={previewLoading}
                    startIcon={
                        previewLoading ? <CircularProgress size={16} color="inherit" /> : undefined
                    }
                    sx={{
                        alignSelf: 'flex-start',
                        '@media (hover: none) and (pointer: coarse)': { minHeight: 48 },
                    }}
                >
                    Preview signed contract
                </Button>
            )}

            {signing && clientDone && !mrtDone && (
                <Alert severity="warning">
                    {data.viewer_is_mrt_signer
                        ? 'The client has signed. Your signature is still needed to complete the contract.'
                        : `The client has signed — waiting for ${mrtSignerName} to sign.`}
                </Alert>
            )}

            {!signing && data.viewer_can_sign && (
                <Button
                    variant="contained"
                    onClick={() => setStartConfirmOpen(true)}
                    disabled={starting}
                    startIcon={starting ? <CircularProgress size={16} color="inherit" /> : undefined}
                    sx={{
                        alignSelf: 'flex-start',
                        '@media (hover: none) and (pointer: coarse)': { minHeight: 48 },
                    }}
                >
                    Start contract signing
                </Button>
            )}

            {!signing && !data.viewer_can_sign && (
                <Typography variant="body2" color="text.secondary">
                    No contract signing has been started yet.
                </Typography>
            )}

            {signing && !fullySigned && data.viewer_is_mrt_signer && (
                <Button
                    variant="outlined"
                    color="error"
                    onClick={() => setConfirmOpen(true)}
                    disabled={cancelling}
                    startIcon={
                        cancelling ? <CircularProgress size={16} color="inherit" /> : undefined
                    }
                    sx={{
                        alignSelf: 'flex-start',
                        '@media (hover: none) and (pointer: coarse)': { minHeight: 48 },
                    }}
                >
                    Cancel signing
                </Button>
            )}

            {signing && !mrtDone && data.viewer_is_mrt_signer && embedSlug && (
                <React.Suspense fallback={<CircularProgress size={24} />}>
                    <DocusealForm
                        src={docusealSigningUrl(embedSlug)}
                        email={signing.mrt_signer?.email}
                        onComplete={handleEmbedComplete}
                    />
                </React.Suspense>
            )}

            {signing && !mrtDone && !data.viewer_is_mrt_signer && (
                <Typography variant="body2" color="text.secondary">
                    Signing in progress — {mrtSignerName} has started the signature flow.
                </Typography>
            )}

            {signing && mrtDone && !clientDone && (
                <Typography variant="body2" color="text.secondary">
                    {data.viewer_is_mrt_signer
                        ? 'Your signature is recorded. Waiting for the client to sign.'
                        : 'MRT signature complete. Waiting for the client to sign.'}
                </Typography>
            )}

            <Confirm
                isOpen={confirmOpen}
                loading={cancelling}
                title="Cancel contract signing?"
                content="The contract signing will be voided and can no longer be signed. You can start a new signing afterwards."
                onConfirm={handleCancel}
                onClose={() => setConfirmOpen(false)}
            />

            <Confirm
                isOpen={startConfirmOpen}
                loading={starting}
                title="Start contract signing?"
                content="This will send an email with the contract to sign to the client. Do you want to continue?"
                confirm="Start"
                cancel="Cancel"
                confirmColor="warning"
                onConfirm={() => {
                    setStartConfirmOpen(false);
                    void handleStart();
                }}
                onClose={() => setStartConfirmOpen(false)}
            />

            <ContractSignerDialog
                open={pickerOpen}
                candidates={data.signer_candidates ?? []}
                submitting={starting}
                onClose={() => setPickerOpen(false)}
                onConfirm={(party) => handleStart(party)}
            />

            <QuoteAttachmentPreviewDialog
                attachment={previewOpen ? SIGNED_CONTRACT_ATTACHMENT : null}
                sourceUrl={previewUrl}
                loading={previewLoading}
                onClose={handleClosePreview}
            />
        </Stack>
    );
};

ContractSigningPanel.displayName = 'ContractSigningPanel';
